import { WsPath } from '@bangle.io/ws-path';

export type WorkspaceTransferFileSystem = {
  exists: (wsPath: string) => Promise<boolean>;
};

const COPY_SUFFIX_RE = /^(.*?)(-copy-\d+)?$/;

/**
 * Picks the next free `<base>-copy-<n>` name among siblings. Strips an
 * existing `-copy-<n>` suffix first so cloning a copy continues the series.
 */
export function resolveNextCopyBaseName(
  name: string,
  existingNames: ReadonlySet<string>,
): string {
  const match = name.match(COPY_SUFFIX_RE);
  const base = match?.[1] ?? name;
  let copyNumber = 1;
  let candidate = `${base}-copy-${copyNumber}`;
  while (existingNames.has(candidate)) {
    copyNumber += 1;
    candidate = `${base}-copy-${copyNumber}`;
  }
  return candidate;
}

/**
 * Finds an available destination path without overwriting. Uses
 * `<base>-copy-<n>.<ext>` suffixes to match note cloning behavior.
 * Returns the desired path unchanged when it is free.
 */
export async function resolveAvailableTransferWsPath(
  fileSystem: WorkspaceTransferFileSystem,
  desiredWsPath: string,
): Promise<{ wsPath: string; renamed: boolean }> {
  const base = WsPath.assertFile(desiredWsPath);
  if (!(await fileSystem.exists(base.wsPath))) {
    return { wsPath: base.wsPath, renamed: false };
  }
  for (let attempt = 1; attempt <= 100; attempt += 1) {
    const candidate = base.replaceFileName(
      `${base.fileNameWithoutExtension}-copy-${attempt}${base.extension}`,
    );
    if (!(await fileSystem.exists(candidate.wsPath))) {
      return { wsPath: candidate.wsPath, renamed: true };
    }
  }
  return { wsPath: base.wsPath, renamed: false };
}

/**
 * Resolves bulk transfer destinations, preserving relative structure for
 * folder moves. Each source maps to destDir + fileName (single) or
 * destDir + relative suffix (folder descendants).
 */
export async function resolveBulkTransferDestinations(
  fileSystem: WorkspaceTransferFileSystem,
  pairs: readonly { oldWsPath: string; desiredNewWsPath: string }[],
): Promise<{ oldWsPath: string; newWsPath: string; renamed: boolean }[]> {
  const resolved: { oldWsPath: string; newWsPath: string; renamed: boolean }[] =
    [];
  const claimed = new Set<string>();
  for (const pair of pairs) {
    const base = WsPath.assertFile(pair.desiredNewWsPath);
    let candidate = base.wsPath;
    let renamed = false;
    if (claimed.has(candidate) || (await fileSystem.exists(candidate))) {
      renamed = true;
      let found: string | undefined;
      for (let attempt = 1; attempt <= 100; attempt += 1) {
        const next = base.replaceFileName(
          `${base.fileNameWithoutExtension}-copy-${attempt}${base.extension}`,
        );
        if (
          !claimed.has(next.wsPath) &&
          !(await fileSystem.exists(next.wsPath))
        ) {
          found = next.wsPath;
          break;
        }
      }
      // If no free slot is found, keep the desired path so the durable
      // create fails with `already-existing` instead of silently overwriting.
      candidate = found ?? base.wsPath;
    }
    claimed.add(candidate);
    resolved.push({
      oldWsPath: pair.oldWsPath,
      newWsPath: candidate,
      renamed,
    });
  }
  return resolved;
}
